/**
 * Navigation Guards
 *
 * Route guards for authentication flow
 */

import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { saveRedirectUri, getRedirectUri } from '@/utils';

/**
 * Auth guard - checks if user is authenticated
 * Redirects to login if required auth is missing
 */
export function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore();

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    if (!authStore.isAuthenticated) {
      // Redirect to email input if not authenticated
      next({ name: 'email-input' });
      return;
    }
  }

  // Check if route requires email to be set
  if (to.meta.requiresEmail) {
    if (!authStore.email) {
      // Redirect to email input if email not set
      next({ name: 'email-input' });
      return;
    }
  }

  next();
}

/**
 * Redirect URI interceptor
 * Parses and saves redirect_uri from query params
 */
export function redirectUriInterceptor(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore();

  // Check for redirect_uri in query params
  const redirectUri = to.query.redirect_uri as string | undefined;

  if (redirectUri) {
    // Save to localStorage
    saveRedirectUri(redirectUri);

    // Save to store
    authStore.setReferrerUrl(redirectUri);

    // Remove from URL (clean up)
    const query = { ...to.query };
    delete query.redirect_uri;

    next({
      ...to,
      query,
      replace: true,
    });
    return;
  }

  // Check if we have a saved redirect URI
  const savedRedirectUri = getRedirectUri();
  if (savedRedirectUri && !authStore.referrerUrl) {
    authStore.setReferrerUrl(savedRedirectUri);
  }

  next();
}

/**
 * Step guard (optional)
 * Prevents users from skipping steps in the auth flow
 * Currently disabled - we allow direct navigation
 */
export function stepGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  // For now, allow all navigation
  // This can be enhanced later if needed
  next();
}

/**
 * Page title guard
 * Updates document title based on route meta
 */
export function pageTitleGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const title = to.meta.title as string | undefined;

  if (title) {
    document.title = `${title} | Bono Auth`;
  } else {
    document.title = 'Bono Auth';
  }

  next();
}

/**
 * Combine all guards
 * Apply guards in order
 */
export function setupGuards(router: any): void {
  // Apply guards in order
  router.beforeEach(redirectUriInterceptor);
  router.beforeEach(authGuard);
  router.beforeEach(pageTitleGuard);
}